import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import type {
	ExternalEnrichmentConflictDto,
	ExternalEnrichmentDto,
	LookupBaselineLineDto,
	PersistedLookupRoundDto,
} from '../dto/external-lookup.dto';
import type {
	ExternalArticleAttributeSource,
	ExternalArticleNumberDto,
	ExternalArticleSourceRefDto,
} from '../dto/product-matching.dto';
import { BaseEntity } from './base.entity';
import type { CrossReferenceLineMatchEntity } from './cross-reference-line-match.entity';
import { CrossReferenceRequestEntity } from './cross-reference-request.entity';

/**
 * Where a line sits in the human review workflow.
 *
 * Distinct from the engine's `noMatch` flag on the same row: `noMatch` is what
 * the ENGINE concluded, `reviewStatus` is what a PERSON decided. A line can be
 * `noMatch` and still be confirmed against an item a reviewer knew about.
 */
export type CrossReferenceLineReviewStatus =
	| 'pending'
	| 'confirmed'
	| 'no_match'
	| 'skipped';

/**
 * One external-article line of a cross-reference request.
 *
 * The persisted columns are the `ExternalArticleLineDto` extraction contract
 * flattened onto a table, plus the engine's per-line result summary, plus the
 * review state. Storing the extraction verbatim (including `rawText` and
 * `sourceRef`) is what makes a re-match reproducible: the engine can be re-run
 * over these rows without the original document.
 */
@Index('idx_crrl_request', ['fkCrossReferenceRequestId'])
@Index('idx_crrl_review_status', ['reviewStatus'])
// The review queue's dominant read is "the lines of THIS request that still
// need a person", so the group key leads and the status follows.
@Index('idx_crrl_request_status', ['fkCrossReferenceRequestId', 'reviewStatus'])
@Entity('cross_reference_request_line')
export class CrossReferenceRequestLineEntity extends BaseEntity {
	@Column('int', { name: 'fk_cross_reference_request__id', unsigned: true })
	public fkCrossReferenceRequestId!: number;

	/** Gapless 1..N sequence across the whole source document. */
	@Column('int', { name: 'lineNumber', unsigned: true })
	public lineNumber!: number;

	/** Verbatim source-language record. Always preserved. */
	@Column('text', { name: 'rawText' })
	public rawText!: string;

	/** Cleaned English form; `rawText` is the source-language preservation. */
	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('varchar', { name: 'norm', nullable: true, length: 128 })
	public norm!: string | null;

	@Column('json', { name: 'articleNumbers' })
	public articleNumbers!: Array<ExternalArticleNumberDto>;

	/**
	 * Open key→value map. Canonical keys come from the tenant's matching
	 * profile; unknown keys are preserved but inert in scoring.
	 */
	@Column('json', { name: 'attributes' })
	public attributes!: Record<string, unknown>;

	@Column('json', { name: 'attributeSources', nullable: true })
	public attributeSources!: Record<
		string,
		ExternalArticleAttributeSource
	> | null;

	@Column('json', { name: 'sourceRef', nullable: true })
	public sourceRef!: ExternalArticleSourceRefDto | null;

	@Column('decimal', {
		name: 'quantity',
		precision: 15,
		scale: 4,
		nullable: true,
	})
	public quantity!: number | null;

	@Column('varchar', { name: 'unitOfMeasure', nullable: true, length: 32 })
	public unitOfMeasure!: string | null;

	/**
	 * What the CUSTOMER quoted for this line, when their document carried a
	 * price. Kept beside our own pricing so the draft quote can show both.
	 */
	@Column('decimal', {
		name: 'externalPriceAmount',
		precision: 15,
		scale: 6,
		nullable: true,
	})
	public externalPriceAmount!: string | null;

	@Column('varchar', {
		name: 'externalPriceCurrency',
		nullable: true,
		length: 8,
	})
	public externalPriceCurrency!: string | null;

	/** The extractor's own certainty for THIS line, 0–1. */
	@Column('decimal', {
		name: 'extractionConfidence',
		precision: 5,
		scale: 4,
		nullable: true,
	})
	public extractionConfidence!: string | null;

	/** Anomalies the extractor flagged: merged cells, illegible region, … */
	@Column('text', { name: 'extractionNotes', nullable: true })
	public extractionNotes!: string | null;

	// ── Engine result summary (rewritten by every match run) ────────────

	/**
	 * The engine's verdict that nothing in the catalog is close enough. A
	 * RESULT, not an error — and a more useful one than a low-confidence
	 * suggestion presented as a match.
	 */
	@Column('tinyint', { name: 'noMatch', width: 1, default: () => "'0'" })
	public noMatch!: boolean;

	@Column('decimal', {
		name: 'topConfidence',
		precision: 5,
		scale: 2,
		nullable: true,
	})
	public topConfidence!: string | null;

	/** True when a strong identifier hit let the later passes be skipped. */
	@Column('tinyint', {
		name: 'stoppedAtStrongHit',
		width: 1,
		default: () => "'0'",
	})
	public stoppedAtStrongHit!: boolean;

	/** How many candidates were considered, before truncation to the top N. */
	@Column('int', { name: 'candidateCount', unsigned: true, default: 0 })
	public candidateCount!: number;

	/**
	 * The line-level auto-assign decision (shape test plus the uniqueness rail).
	 * Report-only: nothing in this wave acts on it.
	 */
	@Column('tinyint', {
		name: 'autoAssignEligible',
		width: 1,
		default: () => "'0'",
	})
	public autoAssignEligible!: boolean;

	// ── External enrichment (written by the `external_lookup` step) ─────

	/**
	 * What each external source returned for this line, verbatim after
	 * validation.
	 *
	 * The MERGED values live in `attributes` / `attributeSources` /
	 * `articleNumbers` above, so the matcher needs no new input path. Keeping
	 * the raw returned objects here as well is what makes a re-match
	 * reproducible and a bad source auditable after the fact — without it,
	 * "which source put this wrong attribute on the line" is unanswerable.
	 */
	@Column('json', { name: 'enrichment', nullable: true })
	public enrichment!: Array<ExternalEnrichmentDto> | null;

	/**
	 * External values REJECTED because the line already carried that key.
	 *
	 * Enrichment is fill-only-if-absent: an external source can never overwrite
	 * a fact the customer stated. A reviewer looking at a line that failed to
	 * match still wants to know a source disagreed, so the discarded value is
	 * recorded rather than dropped silently.
	 */
	@Column('json', { name: 'enrichmentConflicts', nullable: true })
	public enrichmentConflicts!: Array<ExternalEnrichmentConflictDto> | null;

	/**
	 * What each SEARCH ROUND of the lookup reported on its own, before the
	 * synthesis call aggregated them.
	 *
	 * Persisted because the aggregated envelope above has one voice and a
	 * reviewer's next question is always "which page said that". A round is also
	 * the unit an exclusion acts on: taking one out and recomputing the line
	 * without it is only possible if the rounds survived the run that produced
	 * them.
	 */
	@Column('json', { name: 'enrichmentRounds', nullable: true })
	public enrichmentRounds!: Array<PersistedLookupRoundDto> | null;

	/**
	 * The line's own data as it stood before this lookup merged into it.
	 *
	 * The merged columns cannot be un-merged by subtraction — identifiers are
	 * de-duplicated on write, so an identifier the customer stated and a source
	 * repeated looks exactly like one the source alone contributed. Excluding a
	 * source therefore rebuilds the line from this snapshot rather than trying to
	 * remove what the merge added.
	 */
	@Column('json', { name: 'enrichmentBaseline', nullable: true })
	public enrichmentBaseline!: LookupBaselineLineDto | null;

	@Column('datetime', { name: 'enrichedAt', nullable: true })
	public enrichedAt!: Date | null;

	/**
	 * Per-line lookup warnings. A source failure is a warning and a skip, never
	 * a step failure, so this is the only place the review UI can find out WHY
	 * a line came back with no enrichment.
	 */
	@Column('json', { name: 'enrichmentWarnings', nullable: true })
	public enrichmentWarnings!: Array<string> | null;

	// ── Review state (written by people, entity-path so `_audit` fires) ──

	@Column('varchar', { name: 'reviewStatus', length: 32, default: 'pending' })
	public reviewStatus!: CrossReferenceLineReviewStatus;

	@Column('int', {
		name: 'fk_item__id_confirmed',
		nullable: true,
		unsigned: true,
	})
	public fkItemIdConfirmed!: number | null;

	@Column('int', { name: 'reviewedByUserId', nullable: true, unsigned: true })
	public reviewedByUserId!: number | null;

	@Column('datetime', { name: 'reviewedAt', nullable: true })
	public reviewedAt!: Date | null;

	@Column('varchar', { name: 'reviewNote', nullable: true, length: 1000 })
	public reviewNote!: string | null;

	@ManyToOne(() => CrossReferenceRequestEntity, (request) => request.lines, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_cross_reference_request__id', referencedColumnName: 'id' },
	])
	public fkCrossReferenceRequest!: CrossReferenceRequestEntity;

	// String-based reference: the match entity imports this one for its own
	// ManyToOne, so a value import here would be a runtime cycle.
	@OneToMany('CrossReferenceLineMatchEntity', 'fkCrossReferenceRequestLine')
	public matches!: Array<CrossReferenceLineMatchEntity>;
}
